"use client";

import React from "react";
import { MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const secrets = [{
  id: "cannolo",
  subtitle: "Il Dolce Segreto di Sciacca\n\n",
  title: "Le Cucchitelle di Sciacca",
  description: "Le cucchitelle sono uno dei dolci più autentici e identitari della tradizione saccense. Un piccolo scrigno di pasta di mandorla che racchiude un cuore sorprendente di zuccata, la marmellata di zucca dal caratteristico colore verde brillante. Nate nei conventi della città già nel XIV secolo, venivano preparate dalle suore come dolce da vendere, diventando nel tempo un simbolo della pasticceria locale. Ancora oggi Sciacca è uno dei pochi luoghi in cui questa ricetta viene custodita e tramandata fedelmente. La loro forma, che ricorda un cucchiaio rovesciato, dà origine al nome. Ogni cucchitella viene modellata a mano, farcita e chiusa con un gesto rapido, rendendo ogni pezzo unico, espressione autentica di artigianalità. Il segreto è nel ripieno: la zuccata, ottenuta da una varietà antica di zucca bianca, coltivata ancora oggi da produttori locali. Una dolcezza delicata, mai invadente, che si conserva nel tempo mantenendo profumo e consistenza. Da Dolci Sapori, le cucchitelle vengono preparate secondo la tradizione, con ingredienti semplici e lavorazioni manuali, per offrire un dolce che non è solo gusto, ma memoria viva del territorio.\n\n\n\n",
  image: "https://dolcisaporisciacca.blueowlmedia.it/wp-content/uploads/2026/03/Cucchitelle-di-Sciacca-psticceria-artigianale-Sicilia-Dolci-Sapori-Sciacca.webp?v=fcb03bcj1vo",
  alt: "Dettaglio di un cannolo siciliano artigianale con ricotta fresca"
}, {
  id: "cassata",
  subtitle: "La Regina Barocca\n",
  title: "La Cassata Siciliana",
  description: "La cassata è la sintesi della storia siciliana: la ricotta romana, il marzapane arabo, il cioccolato spagnolo e i canditi barocchi. Nata come dolce pasquale, oggi è un monumento alla dolcezza disponibile tutto l'anno. La nostra versione prevede un pan di Spagna soffice, imbevuto di un leggero sciroppo, che racchiude un cuore di ricotta freschissima e gocce di cioccolato, il tutto avvolto da una pasta reale di mandorla finissima e decorato con frutta candita selezionata.",
  image: "https://dolcisaporisciacca.blueowlmedia.it/wp-content/uploads/2026/03/Cassata-Siciliana-psticceria-artigianale-Sicilia-Dolci-Sapori-Sciacca.webp?v=20d73bzs9j",
  alt: "Cassata siciliana decorata con frutta candita e pasta reale"
}, {
  id: "mandorla",
  subtitle: "Il Re della Pasticceria\n",
  title: "Il Cannolo Siciliano",
  description: "Le origini del cannolo si perdono nella leggenda, tra i fasti della dominazione araba e le celebrazioni del Carnevale. Il segreto risiede nel contrasto perfetto: una cialda ('scorza') fritta, croccante e dorata, che accoglie al momento del servizio una crema di ricotta di pecora setosa, lavorata con zucchero e un pizzico di cannella. Da Dolci Sapori, rispettiamo l'antica ricetta di Sciacca, guarnendo le estremità con granella di pistacchio o scorza d'arancia candita.",
  image: "https://dolcisaporisciacca.blueowlmedia.it/wp-content/uploads/2026/03/Cannolo-Siciliano-psticceria-artigianale-Sicilia-Dolci-Sapori-Sciacca-2.webp?v=pdel08kk25s",
  alt: "Vassoio di pasticcini di pasta di mandorla siciliana"
}, {
  id: "torte",
  subtitle: "Il Gusto Quotidiano della Tradizione\n",
  title: "Ciminati",
  description: "Dolci semplici e genuini, caratterizzati dalla presenza del sesamo, che dona un profumo intenso e una piacevole croccantezza. Preparati con ingredienti tradizionali e lavorati secondo ricette tramandate, i ciminati rappresentano la pasticceria più autentica, quella di tutti i giorni. Perfetti per accompagnare caffè, tè o momenti di pausa, sono l’espressione più sincera della tradizione siciliana.",
  image: "https://dolcisaporisciacca.blueowlmedia.it/wp-content/uploads/2026/03/Ciminati-psticceria-artigianale-Sicilia-Dolci-Sapori-Sciacca.webp?v=kv16k1zph4l",
  alt: "Torta artigianale decorata per un evento speciale"
}];
export default function ISegretiDellaTradizione() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="isegretidellatradizione" className="bg-background py-20 md:py-32 overflow-hidden" ref={ref}>
      <div className="container mx-auto px-4 md:px-6">
        
        <div className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-primary mb-6">
            I Segreti della Tradizione
          </h2>
          <p className="font-sans text-lg text-muted-foreground leading-relaxed">Ogni dolce racconta una storia antica. Scopri le leggende e la maestria artigianale che rendono uniche le nostre creazioni, tramandate di generazione in generazione nel cuore di Sciacca.</p>
        </div>

        
        <div className="space-y-24 md:space-y-32">
          {secrets.map((item, index) => <div key={item.id} data-index={index} className={`flex flex-col md:flex-row items-center gap-12 lg:gap-20 transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
              
              <div className="w-full md:w-1/2 relative group">
                
                <div className={`absolute top-6 left-6 w-full h-full rounded-2xl -z-10 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2 ${index % 2 === 0 ? "bg-secondary" : "bg-accent/20"}`} />
                
                <div className="relative rounded-2xl overflow-hidden shadow-lg bg-card">
                  <AspectRatio ratio={4 / 3}>
                    <img src={item.image} alt={item.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </AspectRatio>
                </div>
              </div>

              
              <div className="w-full md:w-1/2 space-y-6 text-center md:text-left">
                <div className="space-y-2">
                  <span className="font-serif italic text-xl md:text-2xl text-primary/80 block">
                    {item.subtitle}
                  </span>
                  <h3 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
                    {item.title}
                  </h3>
                </div>

                <Separator className={`bg-primary/30 w-24 h-1 mx-auto md:mx-0 ${index % 2 === 0 ? "bg-primary/30" : "bg-accent"}`} />

                <p className="font-sans text-muted-foreground leading-relaxed text-lg">
                  {item.description}
                </p>

                <div className="pt-4 flex justify-center md:justify-start">
                  <Button asChild size="lg" className="bg-green-600 text-white hover:bg-green-700 transition-all duration-300 group/btn">
                    <Link to={`https://wa.me/393402498369?text=Buongiorno, vorrei ordinare ${item.title}`} newTab>
                      <MessageCircle className="w-5 h-5 mr-2 group-hover/btn:scale-110 transition-transform" />
                      Ordina {item.title} su WhatsApp
                      <ArrowRight className="w-4 h-4 ml-1 opacity-0 -translate-x-2 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}